/**
 * AAAprinters - Wishlist React Hook
 * Manages wishlist state with Supabase integration
 *
 * Type Safety:
 * - product_id in database is TEXT (to support both UUIDs and legacy IDs)
 * - Always ensure product IDs are strings before database operations
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { normalizeProductId, isValidUUID } from '@/lib/utils';

interface WishlistItem {
  id: string;
  product_id: string;
  user_id: string;
  created_at: string;
}

interface UseWishlistReturn {
  wishlist: WishlistItem[];
  wishlistCount: number;
  loading: boolean;
  error: string | null;
  addToWishlist: (productId: string | number) => Promise<boolean>;
  removeFromWishlist: (productId: string | number) => Promise<void>;
  isInWishlist: (productId: string | number) => Promise<boolean>;
  clearWishlist: () => Promise<void>;
}

/**
 * Hook for managing wishlist state
 */
export function useWishlist(): UseWishlistReturn {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch wishlist items
  const fetchWishlist = useCallback(async () => {
    if (!user) {
      setWishlist([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('wishlists')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setWishlist(data || []);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch wishlist');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load wishlist on mount and when user changes
  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Add item to wishlist
  const addToWishlist = useCallback(async (productId: string | number): Promise<boolean> => {
    if (!user?.id) {
      console.warn('Cannot add to wishlist: User not logged in');
      return false;
    }

    setError(null);

    try {
      // Normalize product ID to string (handles both UUID strings and legacy numbers)
      const normalizedId = normalizeProductId(productId);

      // Validate that we have a proper ID
      if (!normalizedId || normalizedId.trim() === '') {
        throw new Error('Invalid product ID: empty or null');
      }

      // Log warning if not a UUID (helps identify data quality issues)
      if (!isValidUUID(normalizedId)) {
        console.warn('Product ID is not a UUID:', normalizedId);
      }

      // Check if already in wishlist
      const { data: existing, error: checkError } = await supabase
        .from('wishlists')
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', normalizedId)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid error when not found

      if (checkError) {
        console.error('Error checking wishlist:', checkError);
        throw checkError;
      }

      // If exists, remove it (toggle off)
      if (existing) {
        await removeFromWishlist(normalizedId);
        return false;
      }

      // Add to wishlist
      const { error: insertError } = await supabase
        .from('wishlists')
        .insert({
          user_id: user.id,
          product_id: normalizedId,
        });

      if (insertError) {
        console.error('Insert error details:', insertError);
        throw insertError;
      }

      // Refresh wishlist
      await fetchWishlist();
      return true;
    } catch (err) {
      console.error('Error adding to wishlist:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to add to wishlist';
      setError(errorMessage);
      return false;
    }
  }, [user?.id, fetchWishlist]);

  // Remove item from wishlist
  const removeFromWishlist = useCallback(async (productId: string | number): Promise<void> => {
    if (!user?.id) {
      console.warn('Cannot remove from wishlist: User not logged in');
      return;
    }

    setError(null);

    try {
      // Normalize product ID to string
      const normalizedId = normalizeProductId(productId);

      if (!normalizedId || normalizedId.trim() === '') {
        throw new Error('Invalid product ID: empty or null');
      }

      const { error: deleteError } = await supabase
        .from('wishlists')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', normalizedId);

      if (deleteError) {
        console.error('Delete error details:', deleteError);
        throw deleteError;
      }

      // Refresh wishlist
      await fetchWishlist();
    } catch (err) {
      console.error('Error removing from wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove from wishlist');
    }
  }, [user?.id, fetchWishlist]);

  // Check if item is in wishlist
  const isInWishlist = useCallback(async (productId: string | number): Promise<boolean> => {
    if (!user?.id) return false;

    try {
      // Normalize product ID to string
      const normalizedId = normalizeProductId(productId);

      if (!normalizedId || normalizedId.trim() === '') {
        console.warn('Invalid product ID for wishlist check');
        return false;
      }

      const { data, error } = await supabase
        .from('wishlists')
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', normalizedId)
        .maybeSingle(); // Use maybeSingle() to avoid error when not found

      if (error) {
        console.error('Error checking wishlist:', error);
        return false;
      }

      return !!data;
    } catch (err) {
      console.error('Error in isInWishlist:', err);
      return false;
    }
  }, [user?.id]);

  // Clear entire wishlist
  const clearWishlist = useCallback(async (): Promise<void> => {
    if (!user?.id) {
      console.warn('Cannot clear wishlist: User not logged in');
      return;
    }

    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('wishlists')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      setWishlist([]);
    } catch (err) {
      console.error('Error clearing wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear wishlist');
    }
  }, [user?.id]);

  return {
    wishlist,
    wishlistCount: wishlist.length,
    loading,
    error,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    clearWishlist,
  };
}
